import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  AccountCredentialIntegrationType,
  interpolateRoute,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

export interface AppFolioMetadata {
  configId: string
  defaultProperty?: string
}

/**
 * AppFolio Integration API Client
 * Handles all AppFolio-specific API operations
 */
export class AppFolioClient extends BaseApiClient {
  /**
   * Create account credentials for AppFolio
   */
  async createCredentials(
    accountIdentifier: string,
    password: string,
    metadata?: AppFolioMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.CREATE,
          { integrationType: AccountCredentialIntegrationType.APPFOLIO },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'POST',
          body: JSON.stringify({ accountIdentifier, password, metadata }),
        })
      },
      {
        errorContext: 'Error creating AppFolio account credentials',
      },
    )
  }

  /**
   * [ADMIN] Create account credentials for AppFolio for a specific company
   */
  async createAdminCredentials(
    companyId: string,
    accountIdentifier: string,
    password: string,
    metadata?: AppFolioMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.CREATE,
          { integrationType: AccountCredentialIntegrationType.APPFOLIO },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify({ accountIdentifier, password, metadata }),
          },
        )
      },
      {
        errorContext: 'Error creating admin AppFolio account credentials',
      },
    )
  }

  /**
   * Update account credentials for AppFolio
   */
  async updateCredentials(
    accountIdentifier: string,
    password: string,
    metadata?: AppFolioMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.UPDATE,
          { integrationType: AccountCredentialIntegrationType.APPFOLIO },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify({ accountIdentifier, password, metadata }),
        })
      },
      {
        errorContext: 'Error updating AppFolio account credentials',
      },
    )
  }

  /**
   * [ADMIN] Update account credentials for AppFolio for a specific company
   */
  async updateAdminCredentials(
    companyId: string,
    accountIdentifier: string,
    password: string,
    metadata?: AppFolioMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.UPDATE,
          { integrationType: AccountCredentialIntegrationType.APPFOLIO },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ accountIdentifier, password, metadata }),
          },
        )
      },
      {
        errorContext: 'Error updating admin AppFolio account credentials',
      },
    )
  }

  /**
   * Disconnect AppFolio integration
   */
  async disconnect(): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.DELETE,
          { integrationType: AccountCredentialIntegrationType.APPFOLIO },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'DELETE',
        })
      },
      {
        errorContext: 'Error disconnecting AppFolio integration',
      },
    )
  }

  /**
   * [ADMIN] Disconnect AppFolio integration for a specific company
   */
  async disconnectAdmin(companyId: string): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.DELETE,
      { integrationType: AccountCredentialIntegrationType.APPFOLIO },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
      },
      {
        errorContext: 'Error disconnecting admin AppFolio integration',
      },
    )
  }
}

// Export singleton instance
export const appFolioClient = new AppFolioClient()
